import { createFileRoute, redirect } from "@tanstack/react-router";

import { ActionPillLink } from "../components/links";
import { PageHeader } from "../components/shell/app-shell";
import { Button } from "../components/ui/button";
import { Card, cardActionClassName, CardHeader } from "../components/ui/card";
import { Metric } from "../components/ui/metric";
import { Badge, Pill } from "../components/ui/pill";
import { Table, type TableColumn } from "../components/ui/table";
import { CONTRACT_STATUS_LABELS, CONTRACTS, type Contract } from "../demo-data/legal";
import { COMING_SOON_SECTIONS } from "../domain/sections";

export const Route = createFileRoute("/legal")({
	// Still to come (COMING_SOON_SECTIONS): whoever opens this page, by a link or by hand, lands on Home instead.
	beforeLoad: () => {
		if (COMING_SOON_SECTIONS.has("legal")) {
			throw redirect({ to: "/", replace: true });
		}
	},
	head: () => ({ meta: [{ title: "Legal & Contracts · Ravnar" }] }),
	staticData: { crumbs: ["Legal & Contracts"] },
	component: LegalPage,
});

const columns: readonly TableColumn<Contract>[] = [
	{ key: "id", header: "Ref", muted: true, cell: contract => contract.id },
	{ key: "party", header: "Counterparty", cell: contract => <span className="font-medium">{contract.party}</span> },
	{ key: "type", header: "Type", cell: contract => <Badge>{contract.type}</Badge> },
	{ key: "value", header: "Value", align: "right", cell: contract => contract.value },
	{ key: "renews", header: "Renews", align: "right", muted: true, cell: contract => contract.renews },
	{
		key: "status",
		header: "Status",
		align: "right",
		cell: contract => <Pill tone={contract.status}>{CONTRACT_STATUS_LABELS[contract.status]}</Pill>,
	},
	{ key: "owner", header: "Owner", align: "right", cell: contract => <Badge>{contract.owner}</Badge> },
];

const renewingCount = CONTRACTS.filter(contract => contract.status !== "ok").length;

function LegalPage() {
	return (
		<>
			<PageHeader
				title="Legal & Contracts"
				description="What you have signed, what renews next, and what nobody has looked at."
				actions={
					<>
						<Button>Policy library</Button>
						<Button variant="primary">Upload contract</Button>
					</>
				}
			/>
			<div className="mb-[22px] grid grid-cols-1 gap-[11px] md:grid-cols-3">
				<Metric label="Active contracts" value={CONTRACTS.length} sub="Across 5 counterparties" />
				<Metric
					label="Renewing in 30 days"
					value={renewingCount}
					sub="Auto-renew unless cancelled"
					tone="warn"
				/>
				<Metric label="Unreviewed clauses" value="14" sub="Flagged by Document Filing" tone="alert" />
			</div>
			<div className="mb-3.5 flex flex-col gap-2">
				<ActionPillLink to="/work" search={{ item: "W-2481" }} tone="alert" count={1}>
					Insurance renewal quote is past due
				</ActionPillLink>
				<ActionPillLink to="/work" search={{ item: "W-2468" }} tone="warn" count={1}>
					Northwind MSA auto-renews in 18 days
				</ActionPillLink>
			</div>
			<Card>
				<CardHeader
					title="Contract library"
					action={
						<button type="button" className={cardActionClassName}>
							Filters
						</button>
					}
				/>
				<Table label="Contract library" columns={columns} rows={CONTRACTS} rowKey={contract => contract.id} />
			</Card>
		</>
	);
}
